import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import parser from 'html-react-parser';
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { translate } from '../Services/translation';
import { FileUploader } from './FileUploader';
import { isPhoneNumberValid } from '../Helpers/isPhoneNumberValid';

const CLASS_PREFIX = 'sauna-form';

const SaunaForm = (props) => {
  const { data, googleReCaptchaProps } = props;
  const { executeRecaptcha } = googleReCaptchaProps;

  const {
    BenchType,
    Description,
    FileSizeLimit,
    GlassType,
    HeaterType,
    Lighting,
    MaxNumberOfFiles,
    Title,
    TotalFilesSizeLimit,
    WallPanelType,
  } = data || {};

  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [telInputReady, setTelInputReady] = useState(false);

  const telInputRef = useRef();

  const showFDBK = requestLoading || requestSuccess || requestError;
  const showForm = !requestSuccess && !requestError;
  const buttonDisabled = requestLoading;

  const shouldTryAgain = requestError && !requestLoading;
  const shouldResetForm = requestSuccess && !requestLoading;

  const tryAgainLabel = translate('saunaForm.action.tryAgain');
  const resetFormLabel = translate('saunaForm.action.resetForm');
  const submitLabel = translate('saunaForm.action.submit');

  const onSubmit = (e) => {
    e.preventDefault();
    const elements = e.target.elements;

    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available');
      return;
    }

    const headers = new Headers();
    headers.append(
      'litium-request-context',
      `{'ChannelSystemId':'${window.__litium.requestContext.channelSystemId}'}`
    );

    executeRecaptcha('saunaForm').then((reCaptchaToken) => {
      setRequestLoading(true);
      if (elements) {
        const formData = new FormData();
        const data = {};
        for (let i = 0; i < elements.length; i++) {
          const element = elements[i];
          if (element.name && element.name !== 'Files') {
            data[element.name] = element.value;
          }
        }

        formData.append('request', JSON.stringify({ ...data, reCaptchaToken }));

        for (let i = 0; i < elements.Files.files.length; i++) {
          formData.append('files', elements.Files.files[i]);
        }

        fetch('/api/sauna/sendrequest', {
          method: 'POST',
          body: formData,
          headers: headers,
          redirect: 'follow',
        })
          .then((response) => {
            if (response.status === 200) {
              setRequestSuccess(true);
            } else {
              setRequestError(true);
            }
          })
          .catch((error) => {
            setRequestError(true);
            console.error('Error:', error);
          })
          .finally(() => {
            setRequestLoading(false);
          });
      }
    });
  };

  // Handle Phone input validation
  const onTelInput = (e) => {
    e.target.setCustomValidity('');
    const isValid = isPhoneNumberValid(e.target.value);
    if (!isValid) {
      e.target.setCustomValidity(translate('saunaForm.action.phoneValidation'));
      e.target.reportValidity();
    }
  };

  useEffect(() => {
    if (telInputReady) {
      telInputRef.current.addEventListener('input', onTelInput);
    }

    return () => {
      if (telInputRef.current) {
        telInputRef.current.removeEventListener('input', onTelInput);
      }
    };
  }, [telInputReady]);

  const ButtonLabel = (
    <>
      {buttonDisabled && (
        <span className="spinner">{translate('general.loading')}</span>
      )}
      {shouldTryAgain ? tryAgainLabel : null}
      {shouldResetForm ? resetFormLabel : null}
      {!buttonDisabled && !shouldTryAgain && !shouldResetForm
        ? submitLabel
        : null}
    </>
  );

  /**
   * Reset form state.
   * Used for "Try again" and "Reset form" buttons.
   */
  const resetForm = () => {
    setRequestSuccess(false);
    setRequestError(false);
  };

  return (
    <>
      <div
        className={`${CLASS_PREFIX}__fdbk-state${
          showFDBK ? ` ${CLASS_PREFIX}__fdbk-state--visible` : ''
        } padding-horizontal-2 padding-vertical-3`}
      >
        {requestLoading ? (
          <span className={`${CLASS_PREFIX}__fdbk-state--loading`}>
            {translate('general.loading')}
          </span>
        ) : null}
        {requestSuccess ? (
          <span className={`${CLASS_PREFIX}__fdbk-state--success`}>
            {translate('saunaForm.message.success')}
          </span>
        ) : null}
        {requestError ? (
          <span className={`${CLASS_PREFIX}__fdbk-state--error`}>
            {translate('saunaForm.message.error')}
          </span>
        ) : null}
      </div>

      <form
        onSubmit={onSubmit}
        className={`${CLASS_PREFIX}__form row validate`}
        name="sauna-form"
      >
        {showForm ? (
          <>
            <div className={`${CLASS_PREFIX}__form__fieldset`}>
              <label
                htmlFor="Name"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.name')}
              </label>
              <input
                name="Name"
                placeholder={translate('saunaForm.placeholder.name')}
                className={`${CLASS_PREFIX}__form__input form__input`}
                required
                type="text"
              />
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="Email"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.email')}
              </label>
              <input
                name="Email"
                placeholder={translate('saunaForm.placeholder.email')}
                className={`${CLASS_PREFIX}__form__input form__input`}
                required
                type="email"
              />
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="Phone"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.phone')}
              </label>
              <input
                name="Phone"
                placeholder={translate('saunaForm.placeholder.phone')}
                className={`${CLASS_PREFIX}__form__input form__input`}
                required
                type="tel"
                ref={(node) => {
                  if (node) {
                    telInputRef.current = node;
                    setTelInputReady(true);
                  }
                }}
              />
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="RoomDimensions"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.roomDimensions')}
              </label>
              <input
                name="RoomDimensions"
                placeholder={translate('saunaForm.placeholder.roomDimensions')}
                className={`${CLASS_PREFIX}__form__input form__input`}
                required
                type="text"
              />
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="Lighting"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.lighting')}
              </label>
              <select
                name="Lighting"
                className={`${CLASS_PREFIX}__form__input form__select`}
              >
                <option value="" hidden>
                  {translate('saunaForm.select.placeholder')}
                </option>
                {Lighting.map((item, index) => (
                  <option key={index} value={item.Value}>
                    {item.Value}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="HeaterType"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.heaterType')}
              </label>
              <select
                name="HeaterType"
                className={`${CLASS_PREFIX}__form__input form__select`}
              >
                <option value="" hidden>
                  {translate('saunaForm.select.placeholder')}
                </option>
                {HeaterType.map((item, index) => (
                  <option key={index} value={item.Value}>
                    {item.Value}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="BenchType"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.benchType')}
              </label>
              <select
                name="BenchType"
                className={`${CLASS_PREFIX}__form__input form__select`}
              >
                <option value="" hidden>
                  {translate('saunaForm.select.placeholder')}
                </option>
                {BenchType.map((item, index) => (
                  <option key={index} value={item.Value}>
                    {item.Value}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="WallPanelType"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.wallPanelType')}
              </label>
              <select
                name="WallPanelType"
                className={`${CLASS_PREFIX}__form__input form__select`}
              >
                <option value="" hidden>
                  {translate('saunaForm.select.placeholder')}
                </option>
                {WallPanelType.map((item, index) => (
                  <option key={index} value={item.Value}>
                    {item.Value}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="GlassType"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.glassType')}
              </label>
              <select
                name="GlassType"
                className={`${CLASS_PREFIX}__form__input form__select`}
              >
                <option value="" hidden>
                  {translate('saunaForm.select.placeholder')}
                </option>
                {GlassType.map((item, index) => (
                  <option key={index} value={item.Value}>
                    {item.Value}
                  </option>
                ))}
              </select>
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <label
                htmlFor="AdditionalInformation"
                className={`${CLASS_PREFIX}__form__label form__label`}
              >
                {translate('saunaForm.label.additionalInformation')}
              </label>
              <textarea
                name="AdditionalInformation"
                placeholder={translate(
                  'saunaForm.placeholder.additionalInformation'
                )}
                className={`${CLASS_PREFIX}__form__textarea form__input`}
              />
            </div>

            <div className={`${CLASS_PREFIX}__input-wrapper`}>
              <FileUploader
                buttonLabel={translate('saunaForm.action.upload')}
                fileSizeError={translate('saunaForm.message.error.fileSize')}
                maxFilesError={translate('saunaForm.message.error.maxFiles')}
                totalFileSizeError={translate(
                  'saunaForm.message.error.totalFileSize'
                )}
                inputName="Files"
                maxFiles={MaxNumberOfFiles}
                fileSizeLimit={`${FileSizeLimit}MB`}
                totalFileSizeLimit={`${TotalFilesSizeLimit}MB`}
              />
            </div>
          </>
        ) : null}
        <div className={`${CLASS_PREFIX}__input-wrapper`}>
          <button
            type={showForm ? 'submit' : 'button'}
            className="button"
            disabled={buttonDisabled}
            onClick={showForm ? undefined : () => resetForm()}
          >
            {ButtonLabel}
          </button>
        </div>
      </form>
    </>
  );
};

const keyPropType = PropTypes.shape({
  Key: PropTypes.string.isRequired,
  Value: PropTypes.string.isRequired,
});

const propTypes = {
  data: PropTypes.shape({
    BenchType: PropTypes.arrayOf(keyPropType),
    Description: PropTypes.string,
    FileSizeLimit: PropTypes.number,
    GlassType: PropTypes.arrayOf(keyPropType),
    HeaterType: PropTypes.arrayOf(keyPropType),
    Lighting: PropTypes.arrayOf(keyPropType),
    MaxNumberOfFiles: PropTypes.number,
    Title: PropTypes.string,
    TotalFilesSizeLimit: PropTypes.number,
    WallPanelType: PropTypes.arrayOf(keyPropType),
  }),
  googleReCaptchaProps: PropTypes.object,
};

SaunaForm.propTypes = propTypes;

SaunaForm.defaultProps = {
  data: {},
  googleReCaptchaProps: {},
};

export default withGoogleReCaptcha(SaunaForm);
